import Papa from "papaparse";
import { PDFDocument, PDFName, PDFArray } from "pdf-lib";
import * as XLSX from "xlsx";
const JSZip = require("jszip");

// File binary signatures (hex strings for the first 4 bytes)
const FILE_SIGNATURES = {
    pdf: "25504446",
    zip: "504B0304",
    xls: "D0CF11E0",
};

// Size Check

function checkSize(file) {
    if (file.size === 0) {
        return {
            valid: false,
            error: "The file is empty (0 bytes). It may be corrupted. Please re-save and try again.",
        };
    }

    return { valid: true };
}


// Reads entire file as ArrayBuffer
function readAsArrayBuffer(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = (e) => {
            if (e.target.error) { reject(e.target.error); return; }
            resolve(e.target.result);
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
    });
}

// Reads first 4 bytes of a file as hex string
function readFileSignature(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = (e) => {
            if (e.target.error) { reject(e.target.error); return; }
            const arr = new Uint8Array(e.target.result);
            const hex = Array.from(arr)
                .map((b) => b.toString(16).toUpperCase().padStart(2, "0"))
                .join("");
            resolve(hex);
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(file.slice(0, 4));
    });
}

// File Signature Check

async function checkFileSignature(file) {
    const ext = file.name.split(".").pop().toLowerCase();

    if (ext === "csv") return { valid: true };
    if (["png", "jpg", "jpeg"].includes(ext)) return { valid: true };

    let signature;
    try {
        signature = await readFileSignature(file);
    } catch {
        return { valid: true };
    }

    if (ext === "pdf" && !signature.startsWith(FILE_SIGNATURES.pdf)) {
        return { valid: false, error: "This file does not appear to be a valid PDF. It may be corrupted or incorrectly named." };
    }

    if (ext === "zip" && !signature.startsWith(FILE_SIGNATURES.zip)) {
        return { valid: false, error: "This file does not appear to be a valid ZIP archive. It may be corrupted or incorrectly named." };
    }

    if (ext === "xlsx" && !signature.startsWith(FILE_SIGNATURES.zip)) {
        return { valid: false, error: "This file does not appear to be a valid XLSX file. It may be corrupted or incorrectly named." };
    }

    if (ext === "xls" && !signature.startsWith(FILE_SIGNATURES.xls)) {
        return { valid: false, error: "This file does not appear to be a valid XLS file. It may be corrupted or incorrectly named." };
    }

    return { valid: true };
}

// Full Internal / Deep Structural Parse

// PDF Full Parse — pdf-lib
// Helper to safely check if a PDF has any content (text streams, form fields/Annots, XObjects)
function hasPDFPageContent(pdfDoc) {
    try {
        const pages = pdfDoc.getPages();
        if (!pages || pages.length === 0) return false;

        for (let page of pages) {
            // 1. Check for standard /Contents
            const contentsRef = page.node.get(PDFName.of("Contents"));
            if (contentsRef) {
                const contentsObj = pdfDoc.context.lookup(contentsRef);
                if (contentsObj) {
                    if (contentsObj instanceof PDFArray) {
                        for (let i = 0; i < contentsObj.size(); i++) {
                            const streamRef = contentsObj.get(i);
                            const streamObj = pdfDoc.context.lookup(streamRef);
                            if (streamObj) {
                                const bytes = typeof streamObj.getContents === "function" ? streamObj.getContents() : streamObj.contents;
                                if (bytes && bytes.length > 0) return true;
                            }
                        }
                    } else {
                        const bytes = typeof contentsObj.getContents === "function" ? contentsObj.getContents() : contentsObj.contents;
                        if (bytes && bytes.length > 0) return true;
                    }
                }
            }

            // 2. Check for ACORD Form Fields / Annotations (/Annots)
            const annotsRef = page.node.get(PDFName.of("Annots"));
            if (annotsRef) return true;

            // 3. Check for Resources / XObjects (Scanned or Form XObjects)
            const resourcesRef = page.node.get(PDFName.of("Resources"));
            if (resourcesRef) return true;
        }

        // If pageCount > 0, consider it valid content (e.g. AcroForms / XFA / complex streams)
        return pages.length > 0;
    } catch {
        return pdfDoc.getPageCount() > 0;
    }
}

// PDF Full Parse — pdf-lib
async function checkPDFStructure(file) {
    try {
        const buffer = await readAsArrayBuffer(file);
        // ignoreEncryption: true allows checking encrypted PDFs without a password
        const pdfDoc = await PDFDocument.load(buffer, { ignoreEncryption: true });

        const pageCount = pdfDoc.getPageCount();
        if (pageCount === 0) {
            return {
                valid: false,
                error: "The PDF file is empty (0 pages). Please upload a valid document.",
            };
        }

        const hasContent = hasPDFPageContent(pdfDoc);
        if (!hasContent) {
            return {
                valid: false,
                error: "The PDF file is blank and contains no text or data. Please upload a valid document.",
            };
        }

        return { valid: true };
    } catch (error) {
        // Fallback: If signature check passed (%PDF header), don't block valid PDFs that browser/viewers open fine
        return { valid: true };
    }
}

// Helper to validate Excel byte arrays (XLSX / XLS)
function validateExcelBytes(bytes, ext) {
    const signature = Array.from(bytes.slice(0, 4))
        .map((b) => b.toString(16).toUpperCase().padStart(2, "0"))
        .join("");

    if (ext === "xlsx" && !signature.startsWith(FILE_SIGNATURES.zip)) {
        throw new Error("Invalid XLSX binary header signature");
    }
    if (ext === "xls" && !signature.startsWith(FILE_SIGNATURES.xls)) {
        throw new Error("Invalid XLS binary header signature");
    }

    const wb = XLSX.read(bytes, { type: "array", bookSheets: true });
    if (!wb.SheetNames || wb.SheetNames.length === 0) {
        throw new Error("Workbook contains no sheets");
    }
}

// ZIP Full Parse — JSZip
async function checkZipStructure(file) {
    let zipContent;
    try {
        const zip = new JSZip();
        zipContent = await zip.loadAsync(file);
    } catch (error) {
        return {
            valid: false,
            error: "The ZIP file is corrupted and cannot be opened. Please check the file and try again.",
        };
    }

    const fileEntries = Object.keys(zipContent.files).filter(
        (name) =>
            !zipContent.files[name].dir &&
            !name.startsWith("__MACOSX/") &&
            !name.includes(".DS_Store")
    );

    if (fileEntries.length === 0) {
        return {
            valid: false,
            error: "The ZIP archive is empty and contains no files.",
        };
    }

    const innerErrors = [];

    for (const fileName of fileEntries) {
        const zipEntry = zipContent.files[fileName];
        const baseName = fileName.split("/").pop();
        let bytes;

        try {
            bytes = await zipEntry.async("uint8array");
        } catch {
            innerErrors.push(`File "${baseName}" inside the ZIP cannot be read or is corrupted.`);
            continue;
        }

        if (bytes.length === 0) {
            innerErrors.push(`File "${baseName}" inside the ZIP is empty (0 bytes).`);
            continue;
        }

        const ext = fileName.split(".").pop().toLowerCase();

        if (ext === "pdf") {
            try {
                const pdfDoc = await PDFDocument.load(bytes, { ignoreEncryption: true });
                const pageCount = pdfDoc.getPageCount();
                if (pageCount === 0) {
                    innerErrors.push(`PDF file "${baseName}" inside the ZIP is empty (0 pages).`);
                    continue;
                }

                const hasContent = hasPDFPageContent(pdfDoc);
                if (!hasContent) {
                    innerErrors.push(`PDF file "${baseName}" inside the ZIP is blank and contains no text or data.`);
                }
            } catch (error) {
                const signature = Array.from(bytes.slice(0, 4))
                    .map((b) => b.toString(16).toUpperCase().padStart(2, "0"))
                    .join("");
                if (!signature.startsWith(FILE_SIGNATURES.pdf)) {
                    innerErrors.push(`PDF file "${baseName}" inside the ZIP is corrupted.`);
                }
            }
        } else if (ext === "xlsx" || ext === "xls" || ext === "xlsm" || ext === "xltx") {
            try {
                validateExcelBytes(bytes, ext);
            } catch (error) {
                const label = ext.toUpperCase();
                innerErrors.push(`Excel (${label}) file "${baseName}" inside the ZIP is corrupted.`);
            }
        } else if (ext === "csv") {
            try {
                let text;
                try {
                    text = new TextDecoder("utf-8", { fatal: false }).decode(bytes);
                } catch {
                    text = String.fromCharCode.apply(null, bytes);
                }
                const results = Papa.parse(text, { skipEmptyLines: true });
                if (results.errors && results.errors.length > 0) {
                    innerErrors.push(`CSV file "${baseName}" inside the ZIP is malformed: ${results.errors[0].message}`);
                } else if (!results.data || results.data.length === 0) {
                    innerErrors.push(`CSV file "${baseName}" inside the ZIP is empty or has no readable rows.`);
                }
            } catch (error) {
                innerErrors.push(`CSV file "${baseName}" inside the ZIP could not be read.`);
            }
        }
    }

    if (innerErrors.length > 0) {
        return {
            valid: false,
            error: innerErrors.join(" "),
        };
    }

    return { valid: true };
}

// XLSX / XLS Full Parse — SheetJS
async function checkExcelStructure(file, ext) {
    try {
        const buffer = await readAsArrayBuffer(file);
        const data = new Uint8Array(buffer);
        validateExcelBytes(data, ext);
        return { valid: true };
    } catch (error) {
        const label = ext === "xlsx" ? "XLSX" : "XLS";
        return {
            valid: false,
            error: `The ${label} file is corrupted or has an invalid internal structure. Please re-save the file and try again.`,
        };
    }
}

// CSV Full Parse — PapaParse
function checkCSVStructure(file) {
    return new Promise((resolve) => {
        Papa.parse(file, {
            skipEmptyLines: true,
            complete: (results) => {
                if (results.errors && results.errors.length > 0) {
                    resolve({
                        valid: false,
                        error: `CSV file is malformed: ${results.errors[0].message} (row ${results.errors[0].row || "unknown"})`,
                    });
                } else if (!results.data || results.data.length === 0) {
                    resolve({
                        valid: false,
                        error: "CSV file is empty or has no readable rows.",
                    });
                } else {
                    resolve({ valid: true });
                }
            },
            error: () => {
                resolve({
                    valid: false,
                    error: "CSV file could not be read. It may be corrupted.",
                });
            },
        });
    });
}



// Validates a single file through size, signature, and deep structural parsing
export async function validateFile(file) {
    const sizeCheck = checkSize(file);
    if (!sizeCheck.valid) return sizeCheck;

    const signatureCheck = await checkFileSignature(file);
    if (!signatureCheck.valid) return signatureCheck;

    const ext = file.name.split(".").pop().toLowerCase();

    if (ext === "pdf") {
        const pdfCheck = await checkPDFStructure(file);
        if (!pdfCheck.valid) return pdfCheck;
    }

    if (ext === "zip") {
        const zipCheck = await checkZipStructure(file);
        if (!zipCheck.valid) return zipCheck;
    }

    if (ext === "xlsx" || ext === "xls" || ext === "xlsm" || ext === "xltx") {
        const excelCheck = await checkExcelStructure(file, ext);
        if (!excelCheck.valid) return excelCheck;
    }

    if (ext === "csv") {
        const csvCheck = await checkCSVStructure(file);
        if (!csvCheck.valid) return csvCheck;
    }

    return { valid: true };
}

// Validates multiple files
export async function validateFiles(files) {
    for (const file of files) {
        const result = await validateFile(file);
        if (!result.valid) return result;
    }
    return { valid: true };
}